<template>
  <article class="card ead-card">
    <div class="card-image ead-card__imagem">
      <figure v-if="curso.imagem && !imagemInvalida" class="image is-16by9">
        <img
          :src="curso.imagem"
          :alt="`Capa do curso ${curso.nome}`"
          @error="imagemInvalida = true"
        />
      </figure>
      <div v-else class="ead-card__imagem-vazia">
        <b-icon icon="school-outline" size="is-large" />
      </div>

      <div class="ead-card__badges">
        <b-tag v-if="curso.obrigatorio" rounded type="is-warning">
          Obrigatório
        </b-tag>
        <b-tag v-if="curso.atrasado" rounded type="is-danger">
          Em atraso
        </b-tag>
      </div>
    </div>

    <div class="card-content">
      <div class="curso-meta">
        <span>
          <b-icon icon="clock-outline" size="is-small" />
          {{ formatarDuracao(curso.cargaHorariaMinutos) }}
        </span>
        <b-tag rounded :type="tipoStatus">{{ textoStatus }}</b-tag>
      </div>

      <div class="curso-conteudo">
        <h2 class="title is-5 mb-2">{{ curso.nome }}</h2>
        <p class="curso-descricao">
          {{ curso.descricao || 'Este curso não possui descrição.' }}
        </p>
      </div>

      <div
        v-if="curso.dataLimite"
        class="data-limite"
        :class="{ 'data-limite--atrasada': curso.atrasado }"
      >
        <b-icon
          :icon="curso.atrasado ? 'alert-circle-outline' : 'calendar-outline'"
          size="is-small"
        />
        <span>
          {{ curso.atrasado ? 'Prazo encerrado em' : 'Concluir até' }}
          <strong>{{ formatarData(curso.dataLimite) }}</strong>
        </span>
      </div>

      <div class="curso-progresso">
        <div class="curso-progresso__cabecalho">
          <span class="has-text-weight-semibold is-size-7">Seu progresso</span>
          <strong>{{ progresso }}%</strong>
        </div>
        <ead-progresso :valor="progresso" />
      </div>

      <div class="curso-acoes">
        <router-link
          class="button is-primary is-fullwidth"
          :to="{ name: 'ead-curso-player', params: { id: curso.id } }"
        >
          <b-icon :icon="iconeAcao" size="is-small" />
          <span>{{ textoAcao }}</span>
        </router-link>
        <a
          v-if="curso.status === 'CONCLUIDO'"
          class="button is-success is-light is-fullwidth"
          :href="`/api/ead-certificados/${curso.id}/pdf`"
          target="_blank"
          rel="noopener noreferrer"
        >
          <b-icon icon="certificate-outline" size="is-small" />
          <span>Baixar certificado</span>
        </a>
      </div>
    </div>
  </article>
</template>

<script>
import EadProgresso from './ead-progresso.vue'

export default {
  name: 'EadCursoCard',
  components: { EadProgresso },
  props: {
    curso: { type: Object, required: true },
  },
  data: () => ({ imagemInvalida: false }),
  computed: {
    progresso() {
      return Math.round(Number(this.curso.progresso) || 0)
    },
    textoStatus() {
      const textos = {
        NAO_INICIADO: 'Não iniciado',
        EM_ANDAMENTO: 'Em andamento',
        CONCLUIDO: 'Concluído',
      }
      return textos[this.curso.status] || this.curso.status
    },
    tipoStatus() {
      const tipos = {
        NAO_INICIADO: 'is-light',
        EM_ANDAMENTO: 'is-info is-light',
        CONCLUIDO: 'is-success is-light',
      }
      return tipos[this.curso.status] || 'is-light'
    },
    textoAcao() {
      if (this.curso.status === 'CONCLUIDO') return 'Rever curso'
      if (this.progresso > 0) return 'Continuar curso'
      return 'Começar curso'
    },
    iconeAcao() {
      if (this.curso.status === 'CONCLUIDO') return 'replay'
      return this.progresso > 0 ? 'play-circle-outline' : 'play-outline'
    },
  },
  watch: {
    'curso.imagem'() {
      this.imagemInvalida = false
    },
  },
  methods: {
    formatarData(data) {
      if (!data) return '—'
      return new Date(data).toLocaleDateString('pt-BR')
    },
    formatarDuracao(valor) {
      const minutos = Number(valor) || 0
      if (minutos < 60) return `${minutos} min`
      const horas = Math.floor(minutos / 60)
      const restante = minutos % 60
      return restante ? `${horas}h ${restante}min` : `${horas}h`
    },
  },
}
</script>

<style scoped>
.ead-card {
  border: 1px solid #dce8e5;
  border-radius: 16px;
  box-shadow: 0 8px 28px rgba(22, 65, 64, 0.055);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  width: 100%;
}

.ead-card:hover {
  box-shadow: 0 14px 30px rgba(22, 65, 64, 0.13);
  transform: translateY(-3px);
}

.ead-card__imagem {
  background: #f5f5f5;
  overflow: hidden;
  position: relative;
}

.ead-card__imagem img {
  height: 100%;
  object-fit: cover;
  transition: transform 0.25s ease;
  width: 100%;
}

.ead-card:hover .ead-card__imagem img {
  transform: scale(1.025);
}

.ead-card__imagem-vazia {
  align-items: center;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #eefaf8, #f5f5f5);
  color: #76b9ae;
  display: flex;
  justify-content: center;
}

.ead-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  left: 0.75rem;
  position: absolute;
  top: 0.75rem;
}

.card-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1rem;
  padding: 1.15rem;
}

.curso-meta,
.curso-meta span,
.curso-progresso__cabecalho {
  align-items: center;
  display: flex;
}

.curso-meta,
.curso-progresso__cabecalho {
  justify-content: space-between;
}

.curso-meta span {
  color: #63787b;
  font-size: 0.75rem;
  gap: 0.3rem;
}

.curso-conteudo {
  flex: 1;
}

.curso-descricao {
  color: #63787b;
  display: -webkit-box;
  font-size: 0.88rem;
  line-height: 1.5;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.data-limite {
  align-items: center;
  background: #fafafa;
  border: 1px solid #ededed;
  border-radius: 10px;
  color: #63787b;
  display: flex;
  font-size: 0.78rem;
  gap: 0.45rem;
  padding: 0.6rem 0.7rem;
}

.data-limite--atrasada {
  background: #fff5f7;
  border-color: rgba(241, 70, 104, 0.25);
  color: #d83255;
}

.curso-progresso__cabecalho {
  margin-bottom: 0.35rem;
}

.curso-progresso__cabecalho strong {
  color: #008f79;
  font-size: 0.8rem;
}

.curso-progresso ::v-deep .progress-wrapper,
.curso-progresso ::v-deep .progress {
  margin-bottom: 0;
}

.curso-acoes {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
